import React, { useEffect, useState } from 'react'
import { Button, Modal, Text } from '@mantine/core'

interface ResultModalProps {
  opened: boolean
  onClose: () => void
  prizeName: string
  hasMoreChances: boolean
}

const resultModalKeyframes = `
  @keyframes modalSlideIn {
    0% {
      transform: scale(0.3) rotate(-10deg);
      opacity: 0;
    }
    50% {
      transform: scale(1.05) rotate(2deg);
    }
    100% {
      transform: scale(1) rotate(0deg);
      opacity: 1;
    }
  }

  @keyframes prizeReveal {
    0% {
      transform: scale(0.5) translateY(20px);
      opacity: 0;
    }
    60% {
      transform: scale(1.1) translateY(-5px);
    }
    100% {
      transform: scale(1) translateY(0);
      opacity: 1;
    }
  }

  @keyframes sparkle {
    0%, 100% {
      transform: scale(1) rotate(0deg);
      opacity: 1;
    }
    50% {
      transform: scale(1.1) rotate(180deg);
      opacity: 0.8;
    }
  }

  @keyframes buttonPulse {
    0%, 100% {
      transform: scale(1);
      box-shadow: 0 4px 12px rgba(255, 90, 90, 0.2);
    }
    50% {
      transform: scale(1.05);
      box-shadow: 0 6px 20px rgba(255, 90, 90, 0.4);
    }
  }

  @keyframes backgroundGlow {
    0%, 100% {
      filter: brightness(1);
    }
    50% {
      filter: brightness(1.1);
    }
  }

  @keyframes floatingParticles {
    0% {
      transform: translateY(0px) translateX(0px) scale(1);
      opacity: 1;
    }
    50% {
      transform: translateY(-10px) translateX(5px) scale(1.1);
      opacity: 0.8;
    }
    100% {
      transform: translateY(-20px) translateX(-5px) scale(0.8);
      opacity: 0.3;
    }
  }
`

const ResultModal: React.FC<ResultModalProps> = ({ opened, onClose, prizeName, hasMoreChances }) => {
  const [showPrize, setShowPrize] = useState(false)
  const [isClosing, setIsClosing] = useState(false)

  useEffect(() => {
    const style = document.createElement('style')
    style.textContent = resultModalKeyframes
    document.head.appendChild(style)
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style)
      }
    }
  }, [])

  useEffect(() => {
    if (opened) {
      setShowPrize(false)
      setIsClosing(false)
      const timer = setTimeout(() => {
        setShowPrize(true)
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [opened])

  const handleButtonClick = async () => {
    setIsClosing(true)
    // 给用户一个短暂的视觉反馈
    await new Promise((resolve) => setTimeout(resolve, 300))
    onClose()
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      withCloseButton={false}
      size="auto"
      zIndex={1001}
      transitionProps={{
        transition: 'fade',
        duration: 300,
      }}
      styles={{
        content: {
          background: 'transparent',
          border: 'none',
          padding: 0,
          maxWidth: '90vw',
        },
        inner: {
          padding: 0,
        },
      }}
    >
      <div
        style={{
          background: 'linear-gradient(135deg, #FF5A5A 0%, #FF8E8E 50%, #FFB6B6 100%)',
          borderRadius: 24,
          padding: '40px 32px',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 12px 48px rgba(255, 90, 90, 0.3), 0 8px 24px rgba(255, 90, 90, 0.2)',
          animation: 'modalSlideIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
          backgroundSize: '200% 200%',
        }}
      >
        {/* 装饰性背景元素 */}
        <div
          style={{
            position: 'absolute',
            top: -20,
            right: -20,
            width: 100,
            height: 100,
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 70%)',
            borderRadius: '50%',
            animation: 'sparkle 2s ease-in-out infinite',
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 120,
            height: 120,
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.03) 70%)',
            borderRadius: '50%',
            animation: 'sparkle 2s ease-in-out infinite 1s',
          }}
        />

        {/* 浮动粒子效果 */}
        {[...Array(6)].map((_, index) => (
          <div
            key={index}
            style={{
              position: 'absolute',
              width: 6,
              height: 6,
              background: 'rgba(255, 255, 255, 0.8)',
              borderRadius: '50%',
              top: `${20 + index * 15}%`,
              left: `${10 + index * 12}%`,
              animation: `floatingParticles ${2 + index * 0.3}s ease-in-out infinite`,
              animationDelay: `${index * 0.2}s`,
            }}
          />
        ))}

        <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <Text
            size="xl"
            fw={700}
            style={{
              color: '#FFFFFF',
              marginBottom: 24,
              fontSize: 28,
              textShadow: '0 3px 6px rgba(0, 0, 0, 0.2)',
              animation: showPrize ? 'prizeReveal 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)' : 'none',
            }}
          >
            🎉 恭喜您获得 🎉
          </Text>

          <div
            style={{
              marginBottom: 32,
              animation: showPrize ? 'prizeReveal 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s both' : 'none',
            }}
          >
            <Text
              size="xl"
              fw={700}
              style={{
                color: '#FFFFFF',
                fontSize: 26,
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.1) 100%)',
                padding: '16px 32px',
                borderRadius: 16,
                display: 'inline-block',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {/* 奖品背景光效 */}
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '200%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                  animation: 'shimmer 1.5s ease-in-out infinite',
                }}
              />
              <span style={{ position: 'relative', zIndex: 1 }}>✨ {prizeName} ✨</span>
            </Text>
          </div>

          <Button
            size="lg"
            variant="white"
            color="red"
            onClick={handleButtonClick}
            loading={isClosing}
            style={{
              width: '100%',
              height: 52,
              borderRadius: 26,
              fontSize: 18,
              fontWeight: 700,
              animation: showPrize && !isClosing ? 'buttonPulse 2s ease-in-out infinite 1s' : 'none',
              background: 'linear-gradient(135deg, #FFFFFF 0%, #F8F8F8 100%)',
              color: '#FF5A5A',
              border: 'none',
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              transform: 'translateY(0)',
              opacity: isClosing ? 0.7 : 1,
            }}
            onMouseDown={(e) => {
              if (!isClosing) {
                e.currentTarget.style.transform = 'translateY(2px) scale(0.98)'
              }
            }}
            onMouseUp={(e) => {
              if (!isClosing) {
                e.currentTarget.style.transform = 'translateY(0) scale(1)'
              }
            }}
            onMouseLeave={(e) => {
              if (!isClosing) {
                e.currentTarget.style.transform = 'translateY(0) scale(1)'
              }
            }}
          >
            {isClosing ? '更新中...' : hasMoreChances ? '🚀 继续抽奖' : '🎯 确定'}
          </Button>

          {/* 提示文字 */}
          {hasMoreChances && !isClosing && (
            <Text
              size="sm"
              style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: 14,
                marginTop: 12,
                textAlign: 'center',
              }}
            >
              点击后将更新您的抽奖次数
            </Text>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default ResultModal
